import React, { useCallback, useMemo, useState } from 'react'

import { useCanvasSelection } from '@sketch/canvas'

import { useCompare } from '../../CompareContext'
import { useRevealChange } from '../../useRevealChange'
import {
  groupLayerChangesByEntity,
  unattributedLayerChanges,
} from '../../groupChanges'
import { EntityChange, EntityChangeKind, LayerChange } from '../../layerTypes'
import { isAbsentFromCanvas } from '../../utils'
import * as Styled from './ChangesPanel.styles'
import { EntityRow } from './EntityRow'
import { LayerRow } from './LayerRow'

/** The panel with only a message in it, for every state short of a result. */
function ChangesMessage({
  children,
  busy,
}: {
  children: React.ReactNode
  busy?: boolean
}) {
  return (
    <Styled.Wrapper aria-busy={busy || undefined}>
      <Styled.Header>
        <Styled.Title>Changes</Styled.Title>
      </Styled.Header>
      <Styled.Message>{children}</Styled.Message>
    </Styled.Wrapper>
  )
}

/**
 * Everything the comparison found, as a list you can click through.
 *
 * The canvas shows the page; this says what is different about it. Seeing
 * which frame changed never tells you why, and the frames worth looking at
 * first are rarely the ones nearest the viewport.
 *
 * Grouped by entity with its layers beneath, because a change is almost always
 * looked for as "what happened to this screen" rather than as a flat list of
 * layers that happens to span the page.
 */
export function ChangesPanel() {
  const comparison = useCompare()
  const reveal = useRevealChange()

  // The canvas's own selection, so a change picked from this list is lit up
  // exactly as the same thing clicked on the canvas would be - one selection,
  // one mode, and the overlays already know how to draw it.
  const { selection, selectEntityByUUID, selectLayerByPathHash } =
    useCanvasSelection()

  const selectedLayerPathHash =
    selection?.kind === 'layer' ? selection.layerPathHash : undefined

  // Selected, it takes the compared version's colour, as the canvas marks that
  // version's frames with it.
  const isViewingNewer = comparison?.versions?.isViewingNewer ?? true
  const isFromComparedVersion = useCallback(
    (kind: EntityChangeKind) => isAbsentFromCanvas(kind, isViewingNewer),
    [isViewingNewer]
  )

  const selectEntity = useCallback(
    (change: EntityChange) => {
      selectEntityByUUID(change.uuid, change.entityKind)
      reveal({ uuid: change.uuid })
    },
    [selectEntityByUUID, reveal]
  )

  const selectLayer = useCallback(
    (layer: LayerChange) => {
      selectLayerByPathHash(layer.uuid, layer.layerPathHash)
      reveal({
        layerPathHash: layer.layerPathHash,
        // A deleted layer has no node here; the frame it was deleted from
        // does, so that is where the camera goes. Null for a loose layer,
        // which leaves the camera where it is rather than travelling to a
        // frame that was never involved.
        entityUUID: layer.entityUUID,
      })
    },
    [selectLayerByPathHash, reveal]
  )

  const entities = comparison?.entities
  const layers = comparison?.layers

  const layersByEntity = useMemo(
    () => groupLayerChangesByEntity(layers ?? []),
    [layers]
  )

  /** The changes that belong to no frame or Section. */
  const loose = useMemo(() => unattributedLayerChanges(layers ?? []), [layers])

  /**
   * Which entities have been folded away, by uuid.
   *
   * Open is the default: the panel exists to say what changed, and making the
   * reader click each frame to find out would hide the answer they came for.
   * Folding is for the frame with forty changed layers in it, where the summary
   * on the row above already says as much as a reader scanning wants.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())

  const toggle = useCallback(
    (uuid: string) =>
      setCollapsed(open => {
        const next = new Set(open)

        if (!next.delete(uuid)) next.add(uuid)

        return next
      }),
    []
  )

  // Nothing to say when no comparison is running - the panel is not a
  // permanent fixture of the sidebar.
  if (!comparison?.versions) return null

  if (comparison.loading) {
    return <ChangesMessage busy>Reading both versions…</ChangesMessage>
  }

  if (comparison.error) {
    return (
      <ChangesMessage>
        These versions could not be compared. Reload to try again.
      </ChangesMessage>
    )
  }

  // Before "nothing changed", as is the next one: a page that is not there
  // would otherwise compare as a page with nothing on it.
  if (comparison.isPageMissing) {
    return (
      <ChangesMessage>
        This page isn’t in the version you’re comparing with.
      </ChangesMessage>
    )
  }

  // Before "nothing changed": a version without its presentation file reads as
  // an empty page, and would compare as unchanged.
  if (comparison.isAwaitingAssets) {
    return (
      <ChangesMessage>
        One of these versions is still being prepared. Reload to try again.
      </ChangesMessage>
    )
  }

  if (!entities?.length && !loose.length) {
    return (
      <ChangesMessage>
        {comparison.isPartial
          ? 'Part of this page could not be read, so it could not be fully compared. Reload to try again.'
          : 'Nothing changed on this page.'}
      </ChangesMessage>
    )
  }

  const total = (entities?.length ?? 0) + loose.length

  return (
    <Styled.Wrapper data-testid="compare-changes-panel">
      <Styled.Header>
        <Styled.Title>Changes</Styled.Title>
        <Styled.Count>
          {/* Every row the reader can click, not just the entities - a count
          that leaves the loose layers out disagrees with the list under it. */}
          {total === 1 ? '1 item' : `${total} items`}
        </Styled.Count>
      </Styled.Header>

      <Styled.List>
        {(entities ?? []).map(entity => (
          <EntityRow
            key={entity.uuid}
            change={entity}
            layers={layersByEntity.get(entity.uuid) ?? []}
            isSelected={
              selection?.kind !== 'layer' && selection?.uuid === entity.uuid
            }
            isOpen={!collapsed.has(entity.uuid)}
            selectedLayerPathHash={selectedLayerPathHash}
            isFromComparedVersion={isFromComparedVersion}
            onToggle={() => toggle(entity.uuid)}
            onSelect={() => selectEntity(entity)}
            onSelectLayer={selectLayer}
          />
        ))}

        {/* Last, because a frame or a Section is how a reader looks for a
        change and a loose layer is the exception. Given a heading of its own so
        the rows beneath are not read as frames, which is what they would look
        like listed flat among them. */}
        {loose.length > 0 && (
          <Styled.Row data-testid="compare-loose-changes">
            <Styled.GroupLabel>On the page</Styled.GroupLabel>
            {loose.map(layer => (
              <LayerRow
                key={layer.layerPathHash}
                change={layer}
                isSelected={selectedLayerPathHash === layer.layerPathHash}
                isFromComparedVersion={isFromComparedVersion(layer.kind)}
                onSelect={() => selectLayer(layer)}
              />
            ))}
          </Styled.Row>
        )}
      </Styled.List>
    </Styled.Wrapper>
  )
}
